import React from 'react';
import { StyleSheet, View } from 'react-native';
import { LoadingDots, noop, theme } from '@sg/garnish';

import { type PaymentMethod } from '@order/graphql';
import { usePaymentMethods, useSubmitPaymentMethod } from '@order/hooks';
import { useStripeKeys } from '@order/integrations';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { ApplePayButton } from './ApplePayButton';
import { FRAME_HEIGHT } from './constants';

export const ApplePayRequest = (props: ApplePayRequestProps) => {
  const isApplePayEnabled = useFeatureFlag('permanent-apple-pay-enabled');

  if (!isApplePayEnabled) {
    return null;
  }

  return <ApplePayRequestPostGuard {...props} />;
};

const ApplePayRequestPostGuard = (props: ApplePayRequestProps) => {
  const { onSuccess } = props;
  const { stripeKey, clientSecret } = useStripeKeys();
  const { paymentMethods } = usePaymentMethods();
  const { isSubmitting, submitPaymentMethod } = useSubmitPaymentMethod({
    onSuccess: onSuccess ?? noop,
  });

  const hasApplePay = paymentMethods.some(checkHasApplePay);

  if (!stripeKey || !clientSecret || hasApplePay) {
    return null;
  }

  if (isSubmitting) {
    return <ApplePayLoading />;
  }

  return (
    <ApplePayButton
      {...props}
      stripeKey={stripeKey}
      clientSecret={clientSecret}
      onCapturePaymentMethodId={submitPaymentMethod}
    />
  );
};

const ApplePayLoading = () => {
  return (
    <View style={styles.loading}>
      <LoadingDots color={theme.colors.WHITE} />
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

// This is getting either removed or updated to reference a new graph field.
function checkHasApplePay(paymentMethod: PaymentMethod) {
  return paymentMethod.nickname === 'Apple Pay';
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  loading: {
    height: FRAME_HEIGHT - theme.spacing['4'],
    marginVertical: theme.spacing['4'],
    backgroundColor: theme.colors.BLACK,
    borderRadius: theme.radius.small,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ApplePayRequestProps = Readonly<{
  amountToCapture: number;
  onSuccess?: (paymentMethod: PaymentMethod) => void;
}>;
